/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { DEFECT_POSITION } from "../../../../../actions/Checks/constants";
import { formatCheck2Frontend } from "../../../../../actions/Checks/util";
import { Check, FruitProps } from "../../../../../actions/Layers/constants";
import useConfig from "../../../../../actions/Tenants/config/configHook";
import { useCreateCheckMutation, usePatchCheckMutation } from "../../../../../hooks/mutations";
import { useLayerChecksInvalidation } from "../../../../../hooks/useLayerChecks";
import { isFilledArray } from "../../../../../utils";
import { callAvosApi } from "../../../../../utils/useAvosApiHook";
import { TenantDefect } from "../../../../Forms/useManagedOptionsHook";
import { toPercentage } from "../../../Layers/fields/formatters";
import { useQcEntities } from "../../QcEntityContext";
import useCheckFlowNavigate from "../../useCheckFlowNavigate";

const sumRipeningClasses = (advance_ripening_classes_form, check: Check) => (advance_ripening_classes_form || []).reduce((acc: number, i) => acc + (Number(check?.[i.name]) || 0), 0);

type RipeningDayProps = {
    day: number;
    check_id: number | false;
    highlight_current?: boolean;
    tenantDefectDefinitions: TenantDefect[];
}

export const RipeningDay = ({ day, check_id, highlight_current = false, tenantDefectDefinitions }: RipeningDayProps) => {
    const { navigateToAnotherCheck } = useCheckFlowNavigate();
    const { check: currentCheck, layer } = useQcEntities();
    const [localCheck, setCheck] = useState<Check | null>(null);
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const patchMutation = usePatchCheckMutation();
    const createMutation = useCreateCheckMutation();
    const config = useConfig();
    const timerRef = useRef(null as any);

    // * Use hook to invalidate layer checks after creating new checks
    const { invalidateLayerChecks } = useLayerChecksInvalidation(params.tenant_id as string, layer?.id);

    const isCurrentCheck = check_id === currentCheck.check_id;
    const location = config.get_check_type_config(layer, currentCheck);

    // *  fetch all check data for this check
    // * if we navigate to another check we will have to fetch the data again
    useEffect(() => {
        if (check_id) {
            callAvosApi(`/checks/${check_id}/`).then((response) => {
                setCheck(formatCheck2Frontend(response.data));
            });
        }
    }, [check_id, isCurrentCheck]);

    const check = isCurrentCheck ? currentCheck : localCheck;

    // if total_ripening_classes is not equal to sample_size, update sample_size
    const use_advance_ripening_classes_as_sample_size = isFilledArray(location?.advance_ripening_classes_form);
    const total_ripening_classes = sumRipeningClasses(location?.advance_ripening_classes_form, check);

    useEffect(() => {
        if (check && use_advance_ripening_classes_as_sample_size && total_ripening_classes > 0) {
            if (String(check.sample_size) !== String(total_ripening_classes)) {
                clearTimeout(timerRef.current as any);

                timerRef.current = setTimeout(() => {
                    patchMutation.mutate({ check, field: "sample_size", value: total_ripening_classes });
                }, 200);
            }
        }
    }, [use_advance_ripening_classes_as_sample_size, total_ripening_classes]);

    const dispatchCreateCheck = () => {
        createMutation.mutate(
            { tenantId, data: { layer_id: layer.id, location: currentCheck.location } },
            {
                onSuccess: (response) => {
                    invalidateLayerChecks();
                    navigateToAnotherCheck(response.check_id, "advance_ripening", true, {}, true);
                },
            }
        );
    };

    const onClick = () => {
        if (check_id === false) {
            dispatchCreateCheck();
            return;
        }

        callAvosApi(`/checks/${currentCheck.check_id}/business-rules`);
        navigateToAnotherCheck(check_id, "advance_ripening", true, {}, true);
    };

    // check how many fruit have at least one internal defect
    let status = "-";
    let internal_defected_fruit = 0;

    if (check_id && check && check.avocados.length > 0) {
        const internal_defects = tenantDefectDefinitions.filter((defect) => defect.position === DEFECT_POSITION.INTERNAL).map((i) => i.defect_id);
        internal_defected_fruit = check.avocados.filter((i: FruitProps) => i.defects.filter((x) => internal_defects.includes(x.defect_id) && x.frequency > 0).length > 0).length;
        status = check.avocados.length > 0 ? "clean" : "not checked";

        if (internal_defected_fruit > 0) {
            status = `${internal_defected_fruit}/${check.avocados.length}`;
        }
    }

    const highlight = isCurrentCheck && highlight_current;
    const glowingEffect = highlight ? "box-shadow: 0px 0px 8px 8px rgba(19, 165, 95, 0.1);" : "";

    let titleBackgroundClass = "bg-light";

    if (highlight) {
        titleBackgroundClass = "bg-active text-white";
    } else if (check_id) {
        titleBackgroundClass = "bg-secondary text-white";
    }

    const external_defects = tenantDefectDefinitions.filter((defect) => defect.position !== DEFECT_POSITION.INTERNAL && check?.defects[defect.defect_id] > 0);
    const has_external_defects = external_defects.length > 0;

    return (
        <>
            <div
                className="cursor-pointer"
                title={`Check ${check_id || "new"}`}
                css={css` ${glowingEffect}`}
                onClick={onClick}
            >
                <div className={`mb-1 p-2 text-center ${titleBackgroundClass}`}>
                    <span>
                        Day {day}
                    </span>
                </div>

                <div
                    className="mb-1 font-weight-bold p-2 position-relative bg-white"
                    css={css`min-height: 250px; font-size: 12px;`}
                >
                    {check_id && check && (
                        <div>
                            {location?.advance_ripening_classes_form?.map((i) => (
                                <div key={i.name} className="d-flex justify-content-between">
                                    <span>{i.label}:</span>

                                    <span>
                                        <b>{toPercentage(check?.[i.name], check.sample_size, 2)}</b>
                                    </span>
                                </div>
                            ))}

                            {check.temperature && (
                                <div className="d-flex justify-content-between">
                                    <span>Temp:</span>
                                    <span>{check.temperature}°</span>
                                </div>
                            )}

                            {check.sample_size && (
                                <div className="d-flex justify-content-between">
                                    <span>Sample size</span>

                                    <span>
                                        <b>{check.sample_size}</b>
                                    </span>
                                </div>
                            )}

                            {location?.advance_ripening_show_external_defects && !has_external_defects && (
                                <div className="d-flex justify-content-between">
                                    <span>External</span>
                                    <span><b>0%</b></span>
                                </div>
                            )}

                            {location?.advance_ripening_show_external_defects && external_defects.map((defect, index) => (
                                <div key={index}>
                                    <div className="d-flex justify-content-between">
                                        <span>{defect.label}</span>

                                        <span>
                                            <b>{toPercentage(check.defects[defect.defect_id], check.sample_size)}</b>
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {check_id && check && (
                    <div
                        css={css`font-size: 12px;`}
                        className={`${(check.avocados.length > 0 && !(internal_defected_fruit > 0) ? "bg-success text-white" : "bg-light")}  d-flex justify-content-between p-2`}
                    >
                        <span>Internal</span>
                        <span>{status}</span>
                    </div>
                )}

                {!check_id && (
                    <div className="bg-light d-flex justify-content-center align-items-center p-2">
                        <div css={css`opacity: 0;font-size: 12px;`}>
                            <span>Place</span>
                            <span>Holder</span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};
